import { useClient } from '@cortex/react-query';
import type { GroundTruth, WorkflowListItem } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { InfoHint } from '../../components/common/InfoHint';
import { PageHeader } from '../../components/common/PageHeader';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/ground-truth/new')({ component: NewGroundTruthPage });

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

/**
 * Parse the textarea into a plain JSON object. Empty / whitespace-only is
 * treated as `null` for `expectedOutput` (the API accepts null) and as an
 * error for `input` (input is required and must be an object).
 */
function parseJson(
  raw: string,
): { ok: true; value: Record<string, unknown> | null } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed === null) return { ok: true, value: null };
    if (typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, error: 'Must be a JSON object (e.g. { "foo": "bar" })' };
    }
    return { ok: true, value: parsed as Record<string, unknown> };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

function NewGroundTruthPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [workflowId, setWorkflowId] = useState('');
  const [scope, setScope] = useState<'workflow' | 'node'>('workflow');
  const [nodeKey, setNodeKey] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [inputText, setInputText] = useState('{\n  \n}');
  const [expectedText, setExpectedText] = useState('');

  const workflows = useQuery({
    queryKey: ['workflows', 'all-for-gt'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });

  // Find the selected workflow's nodes for the node-scope picker. `graphSummary`
  // is null for workflows with no committed version yet, so we guard the lookup.
  const selectedWorkflow = (workflows.data?.items ?? []).find((w) => w.id === workflowId);
  const nodes = selectedWorkflow?.graphSummary?.nodes ?? [];

  const inputParsed = parseJson(inputText);
  const expectedParsed = parseJson(expectedText);
  const inputError = !inputParsed.ok
    ? inputParsed.error
    : inputParsed.value === null
      ? 'Input is required'
      : null;
  const expectedError = expectedParsed.ok ? null : expectedParsed.error;

  const create = useMutation({
    mutationFn: () => {
      if (!inputParsed.ok || inputParsed.value === null || !expectedParsed.ok) {
        return Promise.reject(new Error('Fix JSON errors before submitting'));
      }
      return client.http
        .post('v1/ground-truth', {
          json: {
            workflowId,
            nodeId: scope === 'node' && nodeKey ? nodeKey : undefined,
            input: inputParsed.value,
            expectedOutput: expectedParsed.value,
            name: name.trim() || undefined,
            notes: notes.trim() || undefined,
          },
        })
        .json<GroundTruth>();
    },
    onSuccess: async (g) => {
      try {
        await qc.invalidateQueries({ queryKey: ['ground-truth'] });
      } catch (err) {
        console.error(err);
      }
      try {
        await nav({ to: routes.GROUND_TRUTH, search: { workflowId: g.workflowId } });
      } catch (err) {
        console.error(err);
      }
    },
  });

  const nodeError =
    scope === 'node' && workflowId.length > 0 && nodeKey.length === 0
      ? 'Pick a node, or switch scope to workflow.'
      : null;
  const canSubmit =
    workflowId.length > 0 &&
    inputError === null &&
    expectedError === null &&
    nodeError === null &&
    !create.isPending;

  return (
    <>
      <PageHeader
        title="New ground truth entry"
        actions={<BackButton to={routes.GROUND_TRUTH} label="Ground truth" />}
      />
      <div className="card" style={{ maxWidth: 820 }}>
        <div className="card-body">
          <form
            style={stack3}
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <label className="field">
              <span className="field-label">
                Workflow
                <InfoHint
                  text="Ground truth is workflow-scoped. The same input will be replayed against this workflow during every evaluation that includes this entry."
                  side="right"
                />
              </span>
              <select
                required={true}
                className="select-native"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
              >
                <option value="">— Select a workflow —</option>
                {(workflows.data?.items ?? []).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name} ({w.id.slice(0, 8)})
                  </option>
                ))}
              </select>
            </label>

            <div className="field">
              <span className="field-label">
                Scope
                <InfoHint
                  text={
                    <>
                      <b>Whole workflow:</b> the entry&apos;s expected output is compared against
                      the workflow&apos;s final output (the trigger run&apos;s overall result). Use
                      this for end-to-end correctness.
                      <br />
                      <br />
                      <b>Specific node:</b> the entry is scored against the output of one particular
                      node inside the workflow (e.g. the classifier or retrieval step). Lets you
                      pinpoint which step regressed when a workflow-level score drops. Multiple
                      node-scoped entries per workflow are encouraged.
                    </>
                  }
                  side="right"
                />
              </span>
              <div
                role="radiogroup"
                style={{ display: 'flex', gap: 'var(--sp-3)', marginTop: 'var(--sp-1)' }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-2)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'workflow'}
                    onChange={() => {
                      setScope('workflow');
                      setNodeKey('');
                    }}
                  />
                  <span className="t-sm">Whole workflow</span>
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-2)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="scope"
                    checked={scope === 'node'}
                    onChange={() => setScope('node')}
                    disabled={workflowId.length === 0}
                  />
                  <span className="t-sm">Specific node</span>
                </label>
              </div>
            </div>

            {scope === 'node' ? (
              <label className="field">
                <span className="field-label">
                  Node
                  <InfoHint
                    text={
                      <>
                        Pick the node whose output should be scored. Node keys are the kebab-case
                        identifiers from the workflow graph (e.g. <code>classify</code>,{' '}
                        <code>retrieve</code>, <code>route</code>).
                        <br />
                        <br />
                        The expected output below should match the shape of that node&apos;s output
                        — not the full workflow output.
                      </>
                    }
                    side="right"
                  />
                </span>
                <select
                  required={true}
                  className="select-native"
                  value={nodeKey}
                  onChange={(e) => setNodeKey(e.target.value)}
                  disabled={nodes.length === 0}
                >
                  <option value="">
                    {nodes.length === 0
                      ? '— Selected workflow has no committed graph —'
                      : '— Select a node —'}
                  </option>
                  {nodes.map((n) => (
                    <option key={n.nodeKey} value={n.nodeKey}>
                      {n.nodeKey} ({n.nodeType})
                    </option>
                  ))}
                </select>
                {nodeError ? (
                  <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                    {nodeError}
                  </span>
                ) : null}
              </label>
            ) : null}

            <label className="field">
              <span className="field-label">
                Name (optional)
                <InfoHint
                  text="A short human label for this entry, e.g. 'High-flow stable patient — should PROCEED'. Falls back to a truncated id in the list view if you leave it blank."
                  side="right"
                />
              </span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="happy-path-1"
              />
            </label>
            <label className="field">
              <span className="field-label">
                Input
                <InfoHint
                  text={
                    <>
                      The trigger input for the workflow — the same payload shape your production
                      runs receive. Must be a JSON object.
                      <br />
                      <br />
                      The server computes a SHA-256 inputHash on save so duplicate inputs are
                      detectable.
                    </>
                  }
                  side="right"
                />
              </span>
              <textarea
                required={true}
                className="textarea t-mono"
                rows={8}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                style={{ fontSize: 12 }}
                placeholder={'{\n  "patientId": "p-123",\n  "vitals": { "hr": 92, "spo2": 96 }\n}'}
              />
              {inputError ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  {inputError}
                </span>
              ) : null}
            </label>
            <label className="field">
              <span className="field-label">
                Expected output (optional)
                <InfoHint
                  text={
                    <>
                      The correct answer for the input above. Must be a JSON object, or leave blank.
                      <br />
                      <br />
                      {scope === 'node' ? (
                        <>
                          Because this entry is <b>node-scoped</b>, the expected output is compared
                          against the selected node&apos;s output (not the workflow&apos;s final
                          output).
                        </>
                      ) : (
                        <>
                          Because this entry is <b>workflow-scoped</b>, the expected output is
                          compared against the workflow&apos;s final result.
                        </>
                      )}
                      <br />
                      <br />
                      Leave blank to mark this entry as &apos;awaiting a label&apos; — the harness
                      will still execute the input but cannot score it until an expected output is
                      added (or until you confirm the actual run output as truth).
                    </>
                  }
                  side="right"
                />
              </span>
              <textarea
                className="textarea t-mono"
                rows={8}
                value={expectedText}
                onChange={(e) => setExpectedText(e.target.value)}
                style={{ fontSize: 12 }}
                placeholder={'{\n  "disposition": "PROCEED",\n  "urgency": "LOW"\n}'}
              />
              {expectedError ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  {expectedError}
                </span>
              ) : null}
            </label>
            <label className="field">
              <span className="field-label">
                Notes (optional)
                <InfoHint
                  text="Free-form context: why this is the right answer, what edge case it covers, links to source tickets. Not used by the harness — purely for humans reviewing the dataset."
                  side="right"
                />
              </span>
              <textarea
                className="textarea"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
              <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                {create.isPending ? 'Creating…' : 'Create entry'}
              </button>
              {create.error ? (
                <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                  {(create.error as Error).message}
                </span>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
